import { useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { PaziaModal } from "components/molecules";
import { PaziaInput } from "components/atoms";
import { ModalHeader } from "components/templates/Modals/ModalHeader";
import { ModalPadding } from "components/templates/Modals/ModalPadding";
import { ButtonVariantSelect } from "pages/Shop/Sections/SectionForms/ButtonVariantSelect";
import { InlineButtonAttrs } from "./extensions";

interface InlineButtonModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: InlineButtonAttrs) => void;
  defaultValues?: InlineButtonAttrs;
  isEdit?: boolean;
}

const schema = yup.object({
  label: yup.string().required("Etykieta jest wymagana"),
  href: yup.string().required("Link jest wymagany"),
  variant: yup.string().required(),
});

export const InlineButtonModal = ({
  isOpen,
  onClose,
  onSubmit,
  defaultValues,
  isEdit = false,
}: InlineButtonModalProps) => {
  const methods = useForm<InlineButtonAttrs>({
    resolver: yupResolver(schema) as any,
    defaultValues: defaultValues || {
      label: "",
      href: "",
      variant: "primary",
    },
  });

  useEffect(() => {
    if (isOpen && defaultValues) {
      methods.reset(defaultValues);
    } else if (isOpen && !defaultValues) {
      methods.reset({
        label: "",
        href: "",
        variant: "primary",
      });
    }
  }, [isOpen, defaultValues, methods]);

  const handleSubmit = (data: InlineButtonAttrs) => {
    onSubmit(data);
    onClose();
  };

  return (
    <PaziaModal id="inline-button-modal" onCloseRequest={onClose} isOpen={isOpen} size="md">
      {isOpen && (
        <ModalPadding>
          <ModalHeader>{isEdit ? "Edytuj przycisk" : "Dodaj przycisk"}</ModalHeader>
          <FormProvider {...methods}>
            <div className="grid gap-4">
              <PaziaInput name="label" label="Etykieta" required placeholder="np. Kup teraz" />
              <PaziaInput name="href" label="Link (URL)" required placeholder="np. /kontakt lub https://..." />
              <ButtonVariantSelect name="variant" required />

              <div className="mt-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg border border-slate-300 px-4 py-2 text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
                >
                  Anuluj
                </button>
                <button
                  type="button"
                  onClick={methods.handleSubmit(handleSubmit)}
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isEdit ? "Zapisz" : "Dodaj"}
                </button>
              </div>
            </div>
          </FormProvider>
        </ModalPadding>
      )}
    </PaziaModal>
  );
};
